import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArticleIcon from "@mui/icons-material/Article";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import CommentIcon from "@mui/icons-material/Comment";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SearchIcon from "@mui/icons-material/Search";

import { COLORS } from "../../styles/base/colors";
import BrandPagination from "./BrandPagination";

const formatArticleDate = (value, withTime = false) => {
  if (!value) return "Reciente";
  try {
    return new Intl.DateTimeFormat("es-MX", {
      dateStyle: "medium",
      ...(withTime ? { timeStyle: "short" } : {}),
    }).format(new Date(value));
  } catch (error) {
    return "Reciente";
  }
};

export const ensureArrayValue = (value) =>
  Array.isArray(value)
    ? value
    : value == null
    ? []
    : Array.isArray(value?.data)
    ? value.data
    : [];

const ArticleCard = ({
  article,
  accentColor = COLORS.primary,
  buttonGradient,
  onOpenArticle,
  onToggleLike,
  canLike,
  likePendingId,
  patientId,
  defaultAuthor,
}) => {
  const likes =
    typeof article?.numeroLikes === "number"
      ? article.numeroLikes
      : ensureArrayValue(article?.likedBy).length;
  const commentsCount = ensureArrayValue(article?.comentarios).length;
  const articleId = article?._id || article?.id;
  const likedBy = ensureArrayValue(article?.likedBy).map((id) => `${id}`);
  const isLiked = patientId ? likedBy.includes(`${patientId}`) : false;
  const likeBusy = likePendingId === articleId;
  const likeDisabled = !canLike || !articleId || article?.isFallback;
  const articleTitle = article?.titulo || article?.title || "Artículo";
  const autorNombre =
    article?.autorNombre?.trim() ||
    article?.autor?.nombre?.trim() ||
    article?.autorId?.nombreAMostrar?.trim() ||
    `${article?.autorId?.usuario?.nombre || ""} ${
      article?.autorId?.usuario?.apellidos || ""
    }`.trim() ||
    defaultAuthor?.name ||
    "Nutriólogo";
  const autorImagen =
    article?.autorId?.usuario?.imagen ||
    article?.autorImagen ||
    article?.autorAvatar ||
    defaultAuthor?.avatar ||
    null;
  const description =
    article?.descripcion ||
    article?.description ||
    article?.resumen ||
    article?.detalle ||
    "Sin descripción";
  const tooltipLike = likeDisabled
    ? "Inicia sesión para reaccionar"
    : isLiked
    ? "Quitar me gusta"
    : "Me gusta";
  const heartStyles = isLiked
    ? {
        color: "#ffffff",
        background: "#ef4444",
        hoverBg: "#dc2626",
        shadow: "0 10px 24px rgba(239,68,68,0.45)",
      }
    : {
        color: "#ffffff",
        background: "rgba(15,23,42,0.2)",
        hoverBg: "rgba(15,23,42,0.35)",
        shadow: "0 8px 20px rgba(15,23,42,0.35)",
      };

  const handleOpen = () => onOpenArticle(article);

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={handleOpen}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          handleOpen();
        }
      }}
      sx={{
        borderRadius: 4,
        border: "1px solid #e5e7eb",
        background: "#fff",
        boxShadow: "0 18px 50px rgba(15,23,42,0.08)",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        cursor: "pointer",
        transition: "transform 160ms ease, box-shadow 160ms ease",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 25px 55px rgba(15,23,42,0.18)",
        },
      }}
    >
      <Box sx={{ position: "relative" }}>
        {article?.imagenUrl || article?.image ? (
          <Box
            component="img"
            src={article.imagenUrl || article.image}
            alt={articleTitle}
            sx={{ width: "100%", height: 220, objectFit: "cover" }}
          />
        ) : (
          <Box
            sx={{
              width: "100%",
              height: 220,
              bgcolor: "#f1f5f9",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#94a3b8",
            }}
          >
            <ArticleIcon sx={{ fontSize: 80 }} />
          </Box>
        )}
        <Stack
          direction="row"
          spacing={1}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            background: "rgba(15,23,42,0.75)",
            borderRadius: 999,
            px: 1.5,
            py: 0.75,
            alignItems: "center",
            gap: 1,
          }}
        >
          <Tooltip title={tooltipLike}>
            <span>
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  if (likeDisabled || likeBusy) return;
                  onToggleLike(article, isLiked);
                }}
                disabled={likeDisabled}
                sx={{
                  color: heartStyles.color,
                  backgroundColor: heartStyles.background,
                  boxShadow: heartStyles.shadow,
                  zIndex: 1,
                  minWidth: 36,
                  minHeight: 36,
                  transition:
                    "background-color 160ms ease, box-shadow 160ms ease",
                  "&:hover": {
                    backgroundColor: heartStyles.hoverBg,
                  },
                  "&.Mui-disabled": {
                    color: heartStyles.color,
                    backgroundColor: heartStyles.background,
                    boxShadow: heartStyles.shadow,
                    opacity: 1,
                  },
                }}
              >
                {likeBusy ? (
                  <CircularProgress
                    size={18}
                    sx={{ color: heartStyles.color }}
                  />
                ) : isLiked ? (
                  <FavoriteIcon fontSize="small" />
                ) : (
                  <FavoriteBorderIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
          <Typography
            variant="caption"
            sx={{ color: "#f8fafc", fontWeight: 600 }}
          >
            {likes}
          </Typography>
          <Divider
            orientation="vertical"
            flexItem
            sx={{ borderColor: "rgba(248,250,252,0.25)" }}
          />
          <Tooltip title="Ver comentarios">
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                onOpenArticle(article, true);
              }}
              sx={{ color: "#f8fafc" }}
            >
              <CommentIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Typography
            variant="caption"
            sx={{ color: "#f8fafc", fontWeight: 600 }}
          >
            {commentsCount}
          </Typography>
        </Stack>
      </Box>

      <Box
        sx={{
          p: 3,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          flexGrow: 1,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Avatar
            src={autorImagen || undefined}
            sx={{ bgcolor: accentColor, color: "#fff" }}
          >
            {!autorImagen && (autorNombre || "N").slice(0, 2).toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {autorNombre}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Publicado {formatArticleDate(article?.createdAt)}
            </Typography>
          </Box>
        </Stack>

        <Tooltip title={articleTitle} arrow>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: "#0f172a",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {articleTitle}
          </Typography>
        </Tooltip>

        <Tooltip title={description} arrow>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              minHeight: 72,
            }}
          >
            {description}
          </Typography>
        </Tooltip>

        <Button
          variant="contained"
          endIcon={<OpenInNewIcon fontSize="small" />}
          onClick={(event) => {
            event.stopPropagation();
            handleOpen();
          }}
          sx={{
            alignSelf: "center",
            textTransform: "none",
            fontWeight: 700,
            px: 3,
            py: 1.25,
            borderRadius: 999,
            color: "#fff",
            backgroundImage:
              buttonGradient ||
              `linear-gradient(120deg, ${accentColor} 0%, ${accentColor} 100%)`,
            boxShadow: "0 15px 35px rgba(15,23,42,0.18)",
            "&:hover": {
              opacity: 0.92,
              backgroundImage:
                buttonGradient ||
                `linear-gradient(120deg, ${accentColor} 0%, ${accentColor} 100%)`,
              boxShadow: "0 18px 40px rgba(15,23,42,0.22)",
            },
          }}
        >
          Leer todo
        </Button>
      </Box>
    </Box>
  );
};

const ArticlesShowcase = ({
  articles,
  accentColor,
  buttonGradient,
  search,
  onSearchChange,
  loading,
  onOpenArticle,
  onToggleLike,
  canLike,
  likePendingId,
  patientId,
  defaultAuthor,
}) => {
  const pageSize = 4;
  const [page, setPage] = useState(1);
  const sharedHorizontalPadding = { xs: 2.5, md: 3.5 };
  const cardsHorizontalPadding = { xs: 2.5, md: 0 };
  const normalizedSearch = search.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!normalizedSearch) return articles;
    return articles.filter((article) =>
      (article?.titulo || article?.title || "")
        .toLowerCase()
        .includes(normalizedSearch)
    );
  }, [articles, normalizedSearch]);

  useEffect(() => {
    setPage(1);
  }, [normalizedSearch, articles]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * pageSize;
  const pagedArticles = filtered.slice(startIndex, startIndex + pageSize);
  const totalArticles = Array.isArray(articles) ? articles.length : 0;
  const shouldShowSearch = totalArticles > pageSize;

  return (
    <Stack spacing={3}>
      <Stack
        spacing={2}
        direction={{ xs: "column", lg: "row" }}
        alignItems={{ xs: "stretch", lg: "center" }}
        justifyContent="space-between"
        sx={{
          borderRadius: 4,
          border: "1px solid rgba(15,23,42,0.08)",
          background: "linear-gradient(120deg, rgba(248,250,252,0.95), #fff)",
          px: sharedHorizontalPadding,
          py: { xs: 2.75, md: 2.5 },
        }}
      >
        <Stack direction="row" spacing={2.5} alignItems="center">
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 3,
              display: "grid",
              placeItems: "center",
              background: `${accentColor}15`,
              color: accentColor,
            }}
          >
            <ArticleIcon />
          </Box>
          <Box>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{ letterSpacing: 1.5 }}
            >
              Todos los artículos
            </Typography>
            <Typography variant="h5" fontWeight={800} color="text.primary">
              Conocimiento compartido por este nutriólogo
            </Typography>
          </Box>
        </Stack>
      </Stack>

      {shouldShowSearch && (
        <Box sx={{ px: cardsHorizontalPadding }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Buscar por título"
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#94a3b8" }} />
                </InputAdornment>
              ),
              sx: {
                borderRadius: 3,
                backgroundColor: "#fff",
              },
            }}
            sx={{
              width: {
                xs: "100%",
                md: "75%",
              },
            }}
          />
        </Box>
      )}

      {loading ? (
        <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
          <CircularProgress color="primary" />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Cargando artículos...
          </Typography>
        </Stack>
      ) : filtered.length ? (
        <Box sx={{ px: cardsHorizontalPadding }}>
          <Grid container spacing={3}>
            {pagedArticles.map((article) => (
              <Grid item xs={12} md={6} key={article?._id || article?.id}>
                <ArticleCard
                  article={article}
                  accentColor={accentColor}
                  buttonGradient={buttonGradient}
                  onOpenArticle={onOpenArticle}
                  onToggleLike={onToggleLike}
                  canLike={canLike}
                  likePendingId={likePendingId}
                  patientId={patientId}
                  defaultAuthor={defaultAuthor}
                />
              </Grid>
            ))}
            {totalPages > 1 && (
              <Grid item xs={12}>
                <Stack alignItems="center" sx={{ pt: 2 }}>
                  <BrandPagination
                    count={totalPages}
                    page={currentPage}
                    onChange={(_, value) => setPage(value)}
                    gradient={buttonGradient}
                    accentColor={accentColor}
                  />
                </Stack>
              </Grid>
            )}
          </Grid>
        </Box>
      ) : (
        <Box sx={{ px: cardsHorizontalPadding }}>
          <Box
            sx={{
              borderRadius: 4,
              border: "1px dashed #cbd5f5",
              background: "rgba(248,250,255,0.6)",
              textAlign: "center",
              py: 6,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Aún no hay artículos publicados
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              Cuando este nutriólogo comparta contenido, lo verás aquí.
            </Typography>
          </Box>
        </Box>
      )}
    </Stack>
  );
};

const ArticleDialog = ({
  open,
  article,
  loading,
  onClose,
  onToggleLike,
  canLike,
  likePendingId,
  patientId,
  defaultAuthor,
}) => {
  if (!article) return null;

  const articleId = article?._id || article?.id;
  const likes =
    typeof article?.numeroLikes === "number"
      ? article.numeroLikes
      : ensureArrayValue(article?.likedBy).length;
  const comments = ensureArrayValue(article?.comentarios);
  const likedBy = ensureArrayValue(article?.likedBy).map((id) => `${id}`);
  const isLiked = patientId ? likedBy.includes(`${patientId}`) : false;
  const likeBusy = likePendingId === articleId;
  const likeDisabled = !canLike || !articleId || article?.isFallback;
  const articleTitle = article?.titulo || article?.title || "Artículo";
  const autorNombre =
    article?.autorNombre?.trim() ||
    article?.autor?.nombre?.trim() ||
    article?.autorId?.nombreAMostrar?.trim() ||
    `${article?.autorId?.usuario?.nombre || ""} ${
      article?.autorId?.usuario?.apellidos || ""
    }`.trim() ||
    defaultAuthor?.name ||
    "Nutriólogo";
  const autorImagen =
    article?.autorId?.usuario?.imagen ||
    article?.autorImagen ||
    article?.autorAvatar ||
    defaultAuthor?.avatar ||
    null;
  const articleBody =
    article?.articulo ||
    article?.contenido ||
    article?.descripcion ||
    article?.description ||
    "";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pr: 2,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>
            {articleTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Publicado {formatArticleDate(article?.createdAt)}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip
            title={
              likeDisabled
                ? "Inicia sesión para reaccionar"
                : isLiked
                ? "Quitar me gusta"
                : "Me gusta"
            }
          >
            <span>
              <Button
                variant={isLiked ? "contained" : "outlined"}
                color="error"
                startIcon={isLiked ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                disabled={likeDisabled || likeBusy}
                onClick={() => onToggleLike(article, isLiked)}
                sx={{ textTransform: "none", fontWeight: 600 }}
              >
                {likeBusy ? "..." : likes}
              </Button>
            </span>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<CommentIcon />}
            sx={{ textTransform: "none" }}
            onClick={() => {
              const container = document.getElementById(
                "article-comments-panel"
              );
              if (container) {
                container.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                });
              }
            }}
          >
            {comments.length}
          </Button>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: "#f8fafc" }}>
        {loading ? (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
            <CircularProgress color="primary" />
          </Stack>
        ) : (
          <Stack spacing={3}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar
                src={autorImagen || undefined}
                sx={{
                  width: 64,
                  height: 64,
                  bgcolor: COLORS.primary,
                  color: "#fff",
                }}
              >
                {!autorImagen && (autorNombre || "N").slice(0, 2).toUpperCase()}
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {autorNombre}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatArticleDate(article?.createdAt, true)}
                </Typography>
              </Box>
            </Stack>

            {article?.imagenUrl || article?.image ? (
              <Box
                component="img"
                src={article.imagenUrl || article.image}
                alt={articleTitle}
                sx={{
                  width: "100%",
                  borderRadius: 3,
                  boxShadow: "0 20px 60px rgba(15,23,42,0.15)",
                }}
              />
            ) : (
              <Box
                sx={{
                  borderRadius: 3,
                  border: "1px dashed #cbd5f5",
                  p: 4,
                  textAlign: "center",
                  color: "#94a3b8",
                }}
              >
                <ArticleIcon sx={{ fontSize: 90 }} />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  Este artículo no tiene imagen destacada
                </Typography>
              </Box>
            )}

            {article?.descripcion && (
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {article.descripcion}
              </Typography>
            )}

            <Typography
              variant="body1"
              sx={{ whiteSpace: "pre-wrap", lineHeight: 1.7 }}
            >
              {articleBody}
            </Typography>

            <Divider />

            <Box id="article-comments-panel">
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Comentarios ({comments.length})
              </Typography>
              {comments.length ? (
                <Stack spacing={2}>
                  {comments
                    .slice()
                    .sort((a, b) => new Date(b.fecha) - new Date(a.fecha))
                    .map((comment) => (
                      <Box
                        key={`${
                          comment?._id || comment?.fecha || Math.random()
                        }`}
                        sx={{
                          borderRadius: 3,
                          border: "1px solid #e2e8f0",
                          background: "#fff",
                          p: 2,
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 700 }}
                        >
                          {comment?.nutriologoId?.nombreAMostrar ||
                            "Nutriólogo"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatArticleDate(comment?.fecha, true)}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {comment?.comentario}
                        </Typography>
                      </Box>
                    ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Aún no hay comentarios publicados en este artículo.
                </Typography>
              )}
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: "flex-end" }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { ArticleCard, ArticleDialog };
export default ArticlesShowcase;
